import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_URL } from "../lib/api";
import { PageHeader, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Download, Copy, Mail, Shield, X, CheckCircle2,
  Clock, FileText, Loader2, ExternalLink, Hash,
} from "lucide-react";

const STATUS_TONE = {
  draft: "neutral",
  sent: "warn",
  in_progress: "warn",
  completed: "success",
  voided: "danger",
};
const STATUS_LABEL = {
  draft: "Draft", sent: "Sent", in_progress: "In progress",
  completed: "Completed", voided: "Voided",
};

export default function EnvelopeDetailPage() {
  const { id } = useParams();
  const [env, setEnv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/envelopes/${id}`).then((r) => setEnv(r.data))
      .catch(() => toast.error("Envelope not found"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/envelopes/${id}/send`);
      setEnv(r.data);
      const delivery = r.data?.email_delivery || [];
      const sent = delivery.filter((x) => x.status === "sent").length;
      const stubbed = delivery.filter((x) => x.status === "stubbed").length;
      const failed = delivery.filter((x) => x.status === "failed").length;
      if (sent) toast.success(`Envelope sent — ${sent} email${sent === 1 ? "" : "s"} delivered`);
      else if (stubbed) toast.success("Envelope sent — emails stubbed (no RESEND_API_KEY)");
      else if (failed) toast.error(`Envelope sent but ${failed} email(s) failed: ${delivery[0]?.error || ""}`);
      else toast.success("Envelope sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  const remind = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/envelopes/${id}/remind`);
      const delivery = r.data?.email_delivery || [];
      const sent = delivery.filter((x) => x.status === "sent").length;
      if (sent) toast.success(`Reminder sent to ${sent} signer${sent === 1 ? "" : "s"}`);
      else if (delivery.length === 0) toast.info("No pending signers to remind");
      else toast.success("Reminder queued");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reminder"); }
    finally { setBusy(false); }
  };

  const voidEnv = async () => {
    if (!window.confirm("Void this envelope? Signers can no longer sign.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/envelopes/${id}/void`);
      setEnv(r.data);
      toast.success("Envelope voided");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not void"); }
    finally { setBusy(false); }
  };

  const download = (kind) => {
    const url = `${API_URL}/envelopes/${id}/${kind}.pdf`;
    const token = localStorage.getItem("marianne_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `marianne-${kind}-${id.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Download failed"));
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/sign/env/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Signing link copied");
  };

  if (loading) return (
    <div className="text-sm text-[#4A5568] flex items-center gap-2">
      <Loader2 size={14} className="animate-spin" /> Loading envelope…
    </div>
  );
  if (!env) return (
    <div className="text-sm text-[#4A5568]">
      Envelope not found. <Link to="/app/signatures" className="underline">Back to envelopes</Link>.
    </div>
  );

  const canSend = env.status === "draft";
  const canVoid = ["sent", "in_progress"].includes(env.status);
  const isCompleted = env.status === "completed";

  return (
    <div data-testid="envelope-detail-page">
      <Link to="/app/signatures" className="label-eyebrow inline-flex items-center gap-1.5 mb-4 hover:text-[#2A0F5A]" data-testid="back-to-sigs">
        <ArrowLeft size={12} /> All envelopes
      </Link>
      <PageHeader
        eyebrow={`Envelope · ${env.id.slice(0, 8)}`}
        title={env.title}
        description={env.message || undefined}
        action={
          <div className="flex items-center gap-2">
            <StatusBadge tone={STATUS_TONE[env.status]}>{STATUS_LABEL[env.status]}</StatusBadge>
            {canSend && (
              <Button onClick={send} disabled={busy} className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="send-btn">
                <Send size={14} className="mr-2" /> {busy ? "Sending…" : "Send"}
              </Button>
            )}
            {canVoid && (
              <Button onClick={remind} variant="outline" className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs" data-testid="remind-btn">
                <Mail size={14} className="mr-2" /> {busy ? "Sending…" : "Remind"}
              </Button>
            )}
            {canVoid && (
              <Button onClick={voidEnv} variant="outline" className="rounded-none border-[#E8E3F0] hover:border-[#8a2121] hover:text-[#8a2121] uppercase tracking-widest text-xs" data-testid="void-btn">
                <X size={14} className="mr-2" /> Void
              </Button>
            )}
            {isCompleted && (
              <>
                <Button onClick={() => download("signed")} className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs" data-testid="download-signed-btn">
                  <Download size={14} className="mr-2" /> Signed PDF
                </Button>
                <Button onClick={() => download("certificate")} variant="outline" className="rounded-none border-[#E8E3F0] uppercase tracking-widest text-xs" data-testid="download-cert-btn">
                  <Shield size={14} className="mr-2" /> Certificate
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Signers */}
        <div className="lg:col-span-1 space-y-3">
          <div className="label-eyebrow">Signers</div>
          {env.signers.map((s, i) => (
            <div key={s.id} className="border border-[#E8E3F0] bg-white p-4" data-testid={`signer-card-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-serif text-lg leading-tight">{s.name}</div>
                  <div className="text-xs text-[#4A5568] mt-0.5">{s.email}</div>
                  <div className="label-eyebrow mt-1.5">
                    {env.ordered !== false ? `Order ${s.order} · ` : ""}{s.role}
                  </div>
                </div>
                <StatusBadge tone={s.status === "signed" ? "success" : s.status === "pending" ? "neutral" : "warn"}>
                  {s.status === "signed" ? "Signed" : "Pending"}
                </StatusBadge>
              </div>
              {s.status === "signed" && (
                <div className="mt-3 text-xs text-[#4A5568]">
                  <CheckCircle2 size={12} className="inline mr-1 text-[#215a3b]" />
                  {new Date(s.signed_at).toLocaleString()} · IP {s.ip || "—"}
                </div>
              )}
              {s.status !== "signed" && env.status !== "draft" && env.status !== "voided" && s.token && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => copyLink(s.token)}
                    className="text-xs text-[#2A0F5A] hover:underline flex items-center gap-1"
                    data-testid={`copy-link-${i}`}
                  >
                    <Copy size={11} /> Copy signing link
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Document body */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-[#E8E3F0] bg-white">
            <div className="px-5 py-3 border-b border-[#E8E3F0] flex items-center justify-between">
              <div className="label-eyebrow">Document</div>
              <div className="text-[10px] font-mono text-[#4A5568] flex items-center gap-1" title="Document SHA-256">
                <Hash size={10} /> {env.document_sha256?.slice(0, 16)}…
              </div>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-[14px] leading-[1.75] font-mono text-[#2A0F5A]/90">
              {env.content}
            </div>
          </div>

          {/* Audit trail */}
          <div className="border border-[#E8E3F0] bg-white">
            <div className="px-5 py-3 border-b border-[#E8E3F0] label-eyebrow">Audit trail</div>
            <div className="divide-y divide-[#E8E3F0]">
              {(env.events || []).slice().reverse().map((e, i) => (
                <div key={e.id || i} className="px-5 py-3 flex items-start gap-3" data-testid={`audit-event-${i}`}>
                  <Clock size={12} className="mt-1 text-[#4A5568] shrink-0" />
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{e.type.replace(/\./g, " · ").replace(/_/g, " ")}</div>
                    <div className="text-xs text-[#4A5568]">
                      {new Date(e.at).toLocaleString()} · {e.actor} · {e.ip || "—"}
                    </div>
                  </div>
                </div>
              ))}
              {(env.events || []).length === 0 && (
                <div className="px-5 py-5 text-xs text-[#4A5568]">No events yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
